import { createAction, props } from '@ngrx/store';
import { MapLayerSummary, MapLayerSummaryByStatus } from './models/map-layer-summary.model';
import { QueryResult } from 'src/app/modules/core/store/models/query.model';
import { Document } from 'src/app/modules/core/store/models/document.model';
import { Folder, Layer, LayerDocumentUIdsSet, LayerExtraGeoFields, LayerTypes, SectionType } from '../cop/models';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { Map, MapLayerView, MapWithState, WeatherDocument } from '../cop/models/map.model';
import { CalculatedLayerFilterOptions, ImageTileLayer } from '../cop/models/map-state.model';
import { MapPosition } from '../../drawing/models/map-position.model';
import { DropActions, DropTargetTypes } from '../../services/map/folder-drag-drop.service.model';
import { BetaOptions, SectionWidgetUserSettings } from './models/map-configuration.model';

//
// Left Drawer
//
export const openCloseLeftDrawerToStore = createAction('[Cop/LeftDrawer/Store] Open Close Drawer', props<{ isOpen: boolean }>());

//
// folders
//
export const newFolder = createAction('[Cop/LeftDrawer] New Folder', props<{ parentFolder: Folder; sectionType?: SectionType }>());
export const storeNewFolderAdditionToStore = createAction(
  '[Cop/LeftDrawer/Store] Store New Folder Addition',
  props<{ mapState: MapWithState; folder: Folder }>()
);
export const editFolderToStore = createAction(
  '[Cop/LeftDrawer/Store] Edit Folder',
  props<{ folder: Folder; isFolderWorkflowSummaryConfigEdit?: boolean }>()
);
export const cancelEditFolderToStore = createAction('[Cop/LeftDrawer/Store] Cancel Edit Folder');
export const storeFolderDeleteToStore = createAction('[Cop/LeftDrawer/Store] Delete Folder', props<{ folder: Folder }>());
export const cancelDeleteFolderToStore = createAction('[Cop/LeftDrawer/Store] Cancel Delete Folder');
export const deleteSelectedFolder = createAction('[Cop/LeftDrawer] Delete Selected Folder');
export const storeDeleteOfSelectedFolderToStore = createAction(
  '[Cop/LeftDrawer/Store] Delete Selected Folder Complete',
  props<{ mapState: MapWithState }>()
);
export const saveFolder = createAction('[Cop/LeftDrawer] Save Folder', props<{ folder: Folder }>());
export const storeSavedFolderToStore = createAction('[Cop/LeftDrawer/Store] Store Saved Folder', props<{ mapState: MapWithState }>());
export const updateDraftFolder = createAction('[Cop/LeftDrawer] Update Draft Folder', props<{ folder: Folder; hideFolderEdit: boolean }>());
export const setDraftMapToStore = createAction(
  '[Cop/LeftDrawer/Store] Store Draft Map',
  props<{ mapState: MapWithState; hideFolderEdit?: boolean }>()
);
export const expandCollapseFolder = createAction('[Cop/LeftDrawer] Expand/Collapse Folder', props<{ folder: Folder }>());
export const expandFolderToStore = createAction('[Cop/LeftDrawer/Store] Expand Folder', props<{ folder: Folder }>());
export const collapseFolderToStore = createAction('[Cop/LeftDrawer/Store] Collapse Folder', props<{ folder: Folder }>());
export const hideShowFolder = createAction('[Cop/LeftDrawer] Hide/Show Folder', props<{ folder: Folder }>());
export const setFolderVisibilityToHiddenToStore = createAction(
  '[Cop/LeftDrawer/Store] Hide Folder',
  props<{ folder: Folder; containedFolders: Folder[]; containedLayers: Layer[] }>()
);
export const setFolderVisibiltyToNotHiddenToStore = createAction(
  '[Cop/LeftDrawer/Store] Show Folder',
  props<{ folder: Folder; containedFolders: Folder[]; containedLayers: Layer[] }>()
);
export const moveFolder = createAction(
  'Cop/LeftDrawer] Move Folder',
  props<{
    sourceFolderUId: string;
    destinationUId: string;
    destinationType: DropTargetTypes;
    moveAction: DropActions;
  }>()
);
export const moveLayer = createAction(
  'Cop/LeftDrawer] Move Layer',
  props<{
    sourceLayerUId: string;
    destinationUId: string;
    destinationType: DropTargetTypes;
    moveAction: DropActions;
  }>()
);
export const moveSummary = createAction(
  'Cop/LeftDrawer] Move Summary',
  props<{
    sourceSummaryUId: string;
    destinationUId: string;
    destinationType: DropTargetTypes;
    moveAction: DropActions;
  }>()
);
export const expandTropicalCycloneToStore = createAction('[Cop/LeftDrawer/Store] Expand Tropical Cyclone', props<{ layerUId: string }>());
export const collapseTropicalCycloneToStore = createAction(
  '[Cop/LeftDrawer/Store] Collapse Tropical Cyclone',
  props<{ layerUId: string }>()
);
export const setTrySaveMapToStore = createAction('[Cop/LeftDrawer/Store] Set Try Save Map', props<{ isTrySaveMap: boolean }>());

//
// Weather animation actions
//
export const startWeatherAnimationToStore = createAction('[Cop/LeftDrawer/Store] Start Weather Animation', props<{ name: string }>());
export const stopWeatherAnimationToStore = createAction('[Cop/LeftDrawer/Store] Stop Weather Animation', props<{ name: string }>());

//
// layers
//
export const newLayer = createAction('[Cop/LeftDrawer] New Layer', props<{ parentFolder: Folder }>());
export const storeNewLayerAddedToStore = createAction(
  '[Cop/LeftDrawer/Store] Store New Layer Added',
  props<{ mapState: MapWithState; layer: Layer }>()
);
export const storeNewLayerCanceledToStore = createAction(
  '[Cop/LeftDrawer/Store] Store New Layer Canceled',
  props<{ mapState: MapWithState }>()
);
export const storeEditedLayerToStore = createAction('[Cop/LeftDrawer/Store] Store Edited Layer', props<{ layer: Layer }>());
export const storeEditedLayerShowQuickFilterToStore = createAction(
  '[Cop/LeftDrawer/Store] Store Edited Layer Show Quickfilter',
  props<{ showQuickFilter: boolean }>()
);
export const updateSelectedLayerQuickfilterSelections = createAction(
  '[Cop/LeftDrawer] Update Quickfilter Selections',
  props<{ layerUId: string; quickFilterSelections?: { [uId: string]: { options: { [uId: string]: { value: string | number }[] } } } }>()
);
export const storeSelectedLayerForOptionsToStore = createAction(
  '[Cop/LeftDrawer/Store] Store Selected Layer For Options',
  props<{ layer?: Layer; showLayerOptionsTab?: string }>()
);
export const cancelEditLayer = createAction('[Cop/LeftDrawer] Cancel Edit Layer');
export const storeCancelEditLayerToStore = createAction('[Cop/LeftDrawer/Store] Store Cancel Edit Layer');
export const deleteLayerToStore = createAction('[Cop/LeftDrawer/Store] Delete Layer', props<{ layer: Layer }>());
export const cancelDeleteLayerToStore = createAction('[Cop/LeftDrawer/Store] Cancel Delete Layer');
export const deleteSelectedLayer = createAction('[Cop/LeftDrawer] Delete Selected Layer');
export const storeDeleteOfSelectedLayerToStore = createAction(
  '[Cop/LeftDrawer/Store] Store Delete of Selected Layer',
  props<{ mapState: MapWithState }>()
);
export const saveWeatherLayers = createAction('[Cop/LeftDrawer] Save Weather Layers For Folder', props<{ parentFolder: Folder }>());
export const saveLayer = createAction('[Cop/LeftDrawer] Save Layer', props<{ layer: Layer; refreshLayersOnly?: LayerDocumentUIdsSet; refreshLabelsOnly?: boolean }>());
export const storeSavedLayerToStore = createAction('[Cop/LeftDrawer/Store] Store Saved Layer', props<{ mapState: MapWithState }>());
export const hideShowLayer = createAction('[Cop/LeftDrawer] Hide/Show Layer', props<{ layer: Layer }>());
export const hideLayers = createAction('[Cop/LeftDrawer] Hide Layers', props<{ layers: Layer[] }>());
export const showLayers = createAction('[Cop/LeftDrawer] Show Layers', props<{ layers: Layer[] }>());
export const setLayerHiddenDocuments = createAction('[Cop/LeftDrawer] Hide Layer', props<{ layerUId: string; documentUIds: string[] }>());

export const showWeatherDocuments = createAction(
  '[Cop/LeftDrawer] Show Weather Documents',
  props<{ layerUId: string; uids: string[] }>()
);
export const hideWeatherDocuments = createAction(
  '[Cop/LeftDrawer] Hide Weather Documents',
  props<{ layerUId: string; uids: string[] }>()
);
export const setLayerVisibilitiesToStore = createAction(
  '[Cop/LeftDrawer/Store] Set Visibility for multiple layers in state',
  props<{ layers: Layer[]; showLayer: boolean }>()
);

export const setLayerOpacityToStore = createAction('[Cop/LeftDrawer/Store] Set Layer Opacity', props<{ layer: Layer; opacity?: number }>());
export const updateFolderPartialVisibilityState = createAction(
  '[Cop/LeftDrawer] Set Folder Partial Visibility',
  props<{ folderUId: string }>()
);
export const updateFoldersPartialVisibilityState = createAction(
  '[Cop/LeftDrawer] Set Folders Partial Visibility',
  props<{ folderUIds: string[] }>()
);
export const updateFolderVisibilityToStore = createAction(
  '[Cop/LeftDrawer/Store] Update Folder Visibility',
  props<{ folderVisibility: { [key: string]: number } }>()
);
export const hideLayerWorkflowStep = createAction(
  '[Cop/LeftDrawer] Hide Layer Workflow Step',
  props<{ stepName: string; layerType?: LayerTypes; sectionUId?: string }>()
);
export const showLayerWorkflowStep = createAction(
  '[Cop/LeftDrawer] Show Layer Workflow Step',
  props<{ stepName: string; layerType?: LayerTypes; sectionUId?: string }>()
);

export const calculateLayerFilterOptions = createAction('[COP/UI] Calculate Map Layers Filter Options', props<{ then: any[] }>());

// Layer Edit Page
export const setMapTileLayersToStore = createAction(
  '[COP Layer/Page/Store] Set Map Image Layers',
  props<{ tileLayers: ImageTileLayer[] }>()
);

export const calculateMapTileLayers = createAction('[COP Layer/Page/Store] Calculate Map Tile Layers');

export const setCalculateLayerFilterOptionsToStore = createAction(
  '[COP Layer/Page/Store] Set Calculated Map Layers Filter Options',
  props<{ layerFilterOptions: CalculatedLayerFilterOptions[] }>()
);
export const setTimedTriggersByLayerAndTypeToStore = createAction(
  '[COP Layer/Page/Store] Set Timed Layer Triggers',
  props<{ triggers?: { layerUId: string; type: 'redraw'; time: number }[] }>()
);
export const updateVisibleWorkflowStepsOfDocumentsForMapLayers = createAction(
  '[COP Layer/Page] Update Visibile Workflow Steps Of Documents For COP Layers',
  props<{
    stepName?: string;
    hide: boolean;
    sectionUId?: string;
    layerUId?: string;
    noRedraw?: boolean;
    clearAll?: boolean;
  }>()
);
export const updateWorkflowStepsExpandedForMapLayers = createAction(
  '[COP Layer/Page] Update Workflow Steps Expanded For COP Layers',
  props<{ stepName: string; expanded: boolean; layerType?: LayerTypes; sectionUId?: string }>()
);
export const updateSectionWidgetUserSettings = createAction(
  '[COP Layer/Page] Update Section Widget User Settings',
  props<{ sectionUId: string; view?: 'workflow-step' | 'layer'; summary?: 'small' | 'large' | 'hide' }>()
);
export const mapMapPositionChanged = createAction('[COP Layer/Page] Map Position Changed Event', props<{ mapPosition: MapPosition }>());
export const updateSecondaryGeoVisibility = createAction(
  '[COP Layer/Page] Update Secondary Geometry Visibility',
  props<{ sectionUId?: string; layerUId?: string; stepUId?: string; action: 'add' | 'remove' | 'remove-all' }>()
);
export const getMapLayerViewFromApi = createAction('[COP/Api] Get Map Layer View From Api');
export const getMapLayerViewFromApiCompleteToStore = createAction(
  '[COP/Api/Store] Get Map Layer View From Api Complete',
  props<{ results: MapLayerView[] }>()
);

//
// datasets
//
export const loadDatasetsForSelect = createAction('[Cop/LeftDrawer] Load Datasets For Select');
export const storeLoadedDatasetsForSelectionToStore = createAction(
  '[Cop/LeftDrawer/Store] Store Datasets Loaded For Selection',
  props<{ results: QueryResult<Dataset> }>()
);

// Map settings and visibility
export const setHiddenDocumentsByLayerToStore = createAction(
  '[Cop/Settings/Store] Set Hiddent Documents By layer to Store',
  props<{ hiddenDocumentsByLayer: LayerDocumentUIdsSet }>()
);
export const setMapSettingsBetaOptionsToStore = createAction(
  '[Cop/Settings/Store] Set Map Settings Beta Options',
  props<{ betaOptions?: BetaOptions }>()
);
export const setLayerQuickFilterSelectionsToStore = createAction(
  '[Cop/Settings/Store] Set Layer Quick Filter Selections',
  props<{ layerQuickFilterSelections: { [layerUId: string]: { [filterUId: string]: { selectedValues: any[] } } } }>()
);
export const setWorkflowStatusVisibilityByLayerTypeAndNameToStore = createAction(
  '[Cop/Settings/Store] Set Workflow Status Visibility Settings',
  props<{ workflowStatusVisibility: { [key: string]: string[] } }>()
);
export const setWorkflowStatusExpandedByLayerTypeAndNameToStore = createAction(
  '[Cop/Settings/Store] Set Workflow Status Expanded Settings',
  props<{ workflowStatusExpanded: { [key: string]: string[] } }>()
);
export const setSectionWidgetUserSettingsToStore = createAction(
  '[Cop/Settings/Store] Set Section Widget User Settings',
  props<{ sectionWidgetUserSettings: { [sectionUId: string]: SectionWidgetUserSettings } }>()
);
export const setGeoFieldsForOptionalDisplayToStore = createAction(
  '[Cop/Settings/Store] Set Geo Fields For Optional Display Settings',
  props<{ geoFieldsForOptionalDisplay: LayerExtraGeoFields[] }>()
);
export const setMapPositionSettingsToStore = createAction(
  '[Cop/Settings/Store] Set Map Position Settings',
  props<{ mapPosition: MapPosition }>()
);

// Workflow summary
export const calculateWorkflowStatusSummary = createAction(
  '[Cop/Workflow] Calculate Workflow Status Summary',
  props<{ summaryItems: MapLayerSummary[] }>()
);

export const setWorkflowStatusSummary = createAction('[Cop/Workflow] Set Workflow Status Summary', props<{ summary: MapLayerSummary[] }>());
export const setWorkflowStatusSummaryCompleteToStore = createAction(
  '[Cop/Workflow/Store] Set Workflow Status Summary Complete',
  props<{ summary: MapLayerSummary[] }>()
);
